// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Import server Config
import { apiLink, checkTtlExpired } from '@configs/serverConfig'

// ** Axios Imports
import axios from 'axios'

export const getCarsList = createAsyncThunk('appCars/getCarsList', async params => {
   params.page = params.page === 0 ? 0 : params.page - 1
   const response = await axios.get(apiLink('getCarsList', params))
   return {
      params,
      data: response.data.cars,
      totalPages: response.data.total_elements
   }
})
export const getCarsAll = createAsyncThunk('appCars/getCarsAll', async () => {
   const params = {sort_list: 'id', sort_order: 'ASC', rows: 1000}
   const response = await axios.get(apiLink('getCarsShortList', params))
   return response.data.cars
})

export const getFleetCarUsageReport = createAsyncThunk('appCars/getFleetCarUsageReport', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/report/car-usage/fleet?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

export const getCarUsageDetail = createAsyncThunk('appCars/getCarUsageDetail', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/report/car-usage/detail?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

export const getCarBasic = createAsyncThunk('appCars/getCarBasic', async (id) => {
   const response = await axios.get(apiLink({ name: 'getCar', value: id }))
   return response.data
})
export const getCar = createAsyncThunk('appCars/getCar', async ({ id/*, types*/ }, { getState }) => {
   const state = getState()
   const existing = state.cars.all.find(car => car.id === id)
   const clear = existing !== undefined ? checkTtlExpired(existing.ttl) : false

   if (existing === undefined || clear) {
      const car_response = await axios.get(apiLink({ name: 'getCar', value: id }))
      //const media_response = await axios.post(apiLink('getFiles', {typeOwner: 'CAR'}), {ids: [id], types})
      return {...car_response.data/*, media: media_response.data.files*/, clear}
   }

   return existing
})

export const addCar = createAsyncThunk('appCars/addCar', async (data, { rejectWithValue }) => {
   try {
      const response = await axios.post(apiLink('addCar'), data)
      return response.data
   } catch (error) {
      if (error.response && error.response.data) {         
         return rejectWithValue(error.response.data)
      }
      return rejectWithValue(error.message)
   }   
})

export const updateCar = createAsyncThunk('appCars/updateCar', async ({data, types}, { getState }) => {   
   const state = getState()      
   const response = await axios.put(apiLink({name: 'updateCar', value: data.id}), data)
   
   if (data.id !== state.cars.selectedCar.id || (state.cars.selectedCar.media === undefined || state.cars.selectedCar.media.length === 0)) {
      const media_response = await axios.post(apiLink('getFiles', {typeOwner: 'CAR'}), {ids: [data.id], types})
      return {...response.data, media: media_response.data.files}   
   }
   
   return response.data
})

export const removeCar = createAsyncThunk('appCars/removeCar', async (id) => {
   const response = await axios.delete(apiLink({name: 'removeCar', value: id}))
   return response
})

export const getCarBrands = createAsyncThunk('appCars/getCarBrands', async () => {
   const response = await axios.get(apiLink('getCarBrands'))
   return response.data.brands
})

export const getOffices = createAsyncThunk('appCars/getOffices', async () => {
   //const response = await axios.get('/app/cars/offices')
   return ['Main office']
})

export const getUniqueCarParams = createAsyncThunk('appCars/getUniqueCarParams', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/get/unique/params?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

export const checkCarDetails = createAsyncThunk('appCars/checkCarDetails', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/check/details?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

// Media files
export const getMedia = createAsyncThunk('appCars/getMedia', async ({car_id, types}, { getState }) => {
   const state = getState()
   const existing = state.cars.all.find(car => car.id === car_id)   

   if (existing === undefined || existing.media === undefined) {
      const response = await axios.post(apiLink('getFiles', {typeOwner: 'CAR'}), {ids: [car_id], types})   
      return {files: response.data.files, car_id, cache: false}      
   }
    
   const loadedTypes = existing.media.map(media => media.type)
   const missingTypes = types.filter(type => !loadedTypes.includes(type))

   if (missingTypes.length > 0) {
      const response = await axios.post(apiLink('getFiles', { typeOwner: 'CAR' }), { ids: [car_id], types: missingTypes })
      return {files: [...existing.media, ...response.data.files], car_id, cache: false}
   }   

   return {files: existing.media, car_id, cache: true}
})
export const addMedia = createAsyncThunk('appCars/addMedia', async ({car_id, type, typeOwner, file}) => {
   const response = await axios({
        method: "post",
        url: apiLink({name: 'carUploadFile', value: car_id}, {type, typeOwner}),
        data: { file },
        headers: { "Content-Type": "multipart/form-data" }
      })
   return {file: response.data.file, car_id}
})
export const removeMedia = createAsyncThunk('appCars/removeMedia', async ({id, car_id}) => {
   const response = await axios.delete(apiLink({name: 'removeFile', value: id}, {typeOwner: 'CAR'}))
   return {id, status: response.status, car_id}
})

// ** Accidents
export const getAccidents = createAsyncThunk('appCars/getAccidents', async (car_id) => {
   const response = await axios.get(apiLink('getAccidentsList', {car_id}))
   return response.data.roadAccidents
})

// ** History
export const carCustomerHistory = createAsyncThunk('appCars/carCustomerHistory', async (params) => {
   const response = await axios.get(apiLink('carCustomerHistory', params))
   return response.data
})
export const changeCarStatus = createAsyncThunk('appCars/changeCarStatus', async (data, { getState }) => {   
   const state = getState()
   const existing = state.cars.all.find(car => car.id === data.id)

   const types = data.types
   delete data.types

   const response = await axios.put(apiLink({name: 'changeCarStatus', value: data.id}, {status: data.status, date_time: data.date_time, note: data.note}, false))

   if (existing === undefined || existing.media === undefined) {
      const media_response = await axios.post(apiLink('getFiles', {typeOwner: 'CAR'}), {ids: [data.id], types})
      return {...response.data, media: media_response.data.files}   
   }
   
   return {...response.data, media: existing.media}
})
export const carByIdCustomerHistory = createAsyncThunk('appCars/carByIdCustomerHistory', async (id) => {
   const response = await axios.get(apiLink({ name: 'carByIdCustomerHistory', value: id}))
   return response.data
})
export const addCarStatusComment = createAsyncThunk('appCars/addCarStatusComment', async (params) => {
   const id = params.id
   delete params.id

   const response = await axios.post(apiLink({ name: 'addCarStatusComment', value: id}, params))
   return response.data
})
export const removeCarStatusComment = createAsyncThunk('appCars/removeCarStatusComment', async (id) => {
   const response = await axios.delete(apiLink({ name: 'removeCarStatusComment', value: id}))
   return response.data
})

// ** export car list
export const getCarsListForExport = createAsyncThunk('appCars/getCarsListForExport', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/list?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})
export const downloadCarsListForExport = createAsyncThunk('appCars/downloadCarsListForExport', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/list/download?env=${process.env.REACT_APP_NODE_ENV}`, data, {
      responseType: 'blob'
   })
   return response.data
})
export const downloadCarsExcelExport = createAsyncThunk('appCars/downloadCarsExcelExport', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/export/excel?env=${process.env.REACT_APP_NODE_ENV}`, data, {
      responseType: 'blob',
      validateStatus: () => true
   })

   const contentType = response.headers['content-type'] || ''
   if (response.status !== 200 || !contentType.includes('spreadsheetml')) {
      let message
      try {
         const text = await response.data.text()
         const json = JSON.parse(text)
         message = json.message || json.error
      } catch (e) {}
      throw new Error(message || `Request failed with status code ${response.status}`)
   }

   const disposition = response.headers['content-disposition'] || ''
   const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)

   return {
      blob: response.data,
      fileName: match ? decodeURIComponent(match[1]) : null
   }
})

// ** get cars list
export const getCarsListForReport = createAsyncThunk('appCars/getCarsListForReport', async () => {
   const params = {sort_list: 'id', sort_order: 'ASC', rows: 1000}
   const response = await axios.get(apiLink('getCarsShortList', params))
   return response.data.cars
})
export const carReportIncome = createAsyncThunk('appCars/carReportIncome', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/finance/cars/income?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

// ** Utils
export const getBrendWithModels = createAsyncThunk('appCars/getBrendWithModels', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/brand-models?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})
export const getSpeeding = createAsyncThunk('appCars/getSpeeding', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/speeding?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

export const getCarsTrackingMileage = createAsyncThunk('appCars/getCarsTrackingMileage', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/cars/get-mileage?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

// ** Reglament work
export const getVisitsByCar = createAsyncThunk('appCars/getVisitsByCar', async car_id => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/visits/get-by-car?env=${process.env.REACT_APP_NODE_ENV}`, {
      car_id,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const addReglamentVisit = createAsyncThunk('appCars/addReglamentVisit', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/visits/add?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const removeReglamentVisit = createAsyncThunk('appCars/removeReglamentVisit', async id => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/visits/remove?env=${process.env.REACT_APP_NODE_ENV}`, {
      id,
      token: localStorage.getItem('token')
   })
   return response.data
})

export const getTasksByCar = createAsyncThunk('appCars/getTasksByCar', async car_id => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/tasks/get-by-car?env=${process.env.REACT_APP_NODE_ENV}`, {
      car_id,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const addReglamentTask = createAsyncThunk('appCars/addReglamentTask', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/tasks/add?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const changeReglamentTaskStatus = createAsyncThunk('appCars/changeReglamentTaskStatus', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/tasks/status?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})

export const getWorksByCar = createAsyncThunk('appCars/getWorksByCar', async car_id => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/works/get-by-car?env=${process.env.REACT_APP_NODE_ENV}`, {
      car_id,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const getWorksByVin = createAsyncThunk('appCars/getWorksByCar', async vin => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/works/get-by-vin?env=${process.env.REACT_APP_NODE_ENV}`, {
      vin,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const addReglamentWork = createAsyncThunk('appCars/addReglamentWork', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/works/add?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const completeReglamentWork = createAsyncThunk('appCars/completeReglamentWork', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/works/finish?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const removeReglamentWork = createAsyncThunk('appCars/removeReglamentWork', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/works/remove?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})

export const getLatestCarWorksRepairAct = createAsyncThunk('appCars/getLatestCarWorksRepairAct', async car_id => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/works/car-latest-works?env=${process.env.REACT_APP_NODE_ENV}`, {
      car_id,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const getReglamentRecords = createAsyncThunk('appCars/getReglamentRecords', async params => {   
   params.page = params.page === 0 ? 0 : params.page - 1
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/records?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...params,
      token: localStorage.getItem('token')
   })

   return {
      params,
      data: response.data.cars,
      types: response.data.types,
      totalPages: response.data.total_elements
   }
})
export const getReglamentExpiredRecords = createAsyncThunk('appCars/getReglamentExpiredRecords', async params => {   
   params.page = params.page === 0 ? 0 : params.page - 1
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/expired?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...params,
      token: localStorage.getItem('token')
   })

   return {
      params,
      data: response.data.cars,
      totalPages: response.data.total_elements
   }
})
export const getReglamentExpired = createAsyncThunk('appCars/getReglamentExpired', async data => {   
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/expired?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})
// ** Reglament settings
export const getReglamentSettings = createAsyncThunk('appCars/getReglamentSettings', async () => {   
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/settings/get?env=${process.env.REACT_APP_NODE_ENV}`, {      
      token: localStorage.getItem('token')      
   })
   return response.data
})
export const updateReglamentSettings = createAsyncThunk('appCars/updateReglamentSettings', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/settings/update?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

// ** Reglament types
export const getReglamentTypes = createAsyncThunk('appCars/getReglamentTypes', async () => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/types/list?env=${process.env.REACT_APP_NODE_ENV}`, {
      token: localStorage.getItem('token')
   })
   return response.data
})
export const addReglamentType = createAsyncThunk('appCars/addReglamentType', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/types/add?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const updateReglamentType = createAsyncThunk('appCars/updateReglamentType', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/types/update?env=${process.env.REACT_APP_NODE_ENV}`, {
      ...data,
      token: localStorage.getItem('token')
   })
   return response.data
})
export const removeReglamentType = createAsyncThunk('appCars/removeReglamentType', async id => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/types/remove?env=${process.env.REACT_APP_NODE_ENV}`, {
      id,
      token: localStorage.getItem('token')
   })
   return response.data
})

// ** Cars maintenance
export const getCarsMaintenanceWorks = createAsyncThunk('appCars/getCarsMaintenanceWorks', async params => {   
   params.page = params.page === 0 ? 0 : params.page - 1
   const response = await axios.post(`https://api.mfauto.com.ua/repair/car-maintenances?env=${process.env.REACT_APP_NODE_ENV}`, params)

   return {
      params,
      data: response.data.response,
      totalPages: response.data.total_elements
   }
})
export const getCarsUsedParts = createAsyncThunk('appCars/getCarsUsedParts', async params => {   
   params.page = params.page === 0 ? 0 : params.page - 1
   const response = await axios.post(`https://api.mfauto.com.ua/repair/car-used-parts?env=${process.env.REACT_APP_NODE_ENV}`, params)

   return {
      params,
      data: response.data.response,
      totalPages: response.data.total_elements
   }
})
export const getReglamentDeviationRecords = createAsyncThunk('appCars/getReglamentDeviationRecords', async data => {
   const response = await axios.post(`https://api.mfauto.com.ua/reglament/deviation/get?env=${process.env.REACT_APP_NODE_ENV}`, data)
   return response.data
})

const getLocalStorageReglamentFetchData = () => {
    const storage = window.localStorage.getItem('reglament')
    return storage !== null ? JSON.parse(storage) : {        
        expired: 0,
        fetch: null
    }
}

export const appCarsSlice = createSlice({
name: 'appCars',
initialState: {
   data: [],
   total: 1,
   params: {},
   media: [],
   all: [],
   brands: [],
   offices: [],
   accidents: [],
   maintenances: [],
   parts: [],
   selectedCar: null,
   reglamentSettings: null,
   reglamentTypes: null,
   reglamentRecordTypes: null,
   quantity: getLocalStorageReglamentFetchData()
},
reducers: {
      changeExpiredQuantity: (state, action) => {
         state.quantity.expired = action.payload
         
         const storage = window.localStorage.getItem('reglament')      
         const storageParse = storage !== null ? JSON.parse(storage) : {}
         localStorage.setItem('reglament', JSON.stringify({                               
               expired: action.payload,
               fetch: storageParse?.fetch
         }))            
      },
      registerReglamentFetch: (state, action) => {
         state.quantity.fetch = action.payload
         
         const storage = window.localStorage.getItem('reglament')      
         const storageParse = storage !== null ? JSON.parse(storage) : {}
         localStorage.setItem('reglament', JSON.stringify({                               
               expired: storageParse?.expired,
               fetch: action.payload
         })) 
      }
   },
   extraReducers: builder => {
      builder
         .addCase(getCarsList.fulfilled, (state, action) => {
            state.data = action.payload.data
            state.params = action.payload.params
            state.total = action.payload.totalPages
         })
         .addCase(getCar.fulfilled, (state, action) => {
            state.selectedCar = action.payload
            const clear = action.payload.clear
            delete action.payload.clear

            if (clear) {
               if (state.all.some((car) => car.id === action.payload.id)) {
                  state.all = state.all.map(car => (car.id === action.payload.id ? {...action.payload, ttl: new Date} : car))
               } else {
                  state.all = [...state.all, {...action.payload, ttl: new Date}]
               }
            } else {
               state.all = [...state.all, {...action.payload, ttl: new Date}]
            }
         })
         .addCase(getCarBasic.fulfilled, (state, action) => {
            state.selectedCar = {...state.selectedCar, ...action.payload}
         })
         .addCase(updateCar.fulfilled, (state, action) => {
            state.selectedCar = {...action.payload}
            state.all = state.all.filter(item => item.id !== action.payload.id)
            state.all = [...state.all, {...action.payload, ttl: new Date}]
         })
         .addCase(getMedia.fulfilled, (state, action) => {    
            state.selectedCar = {...state.selectedCar, media: action.payload.files}
            state.all = state.all.map(car => (car.id === action.payload.car_id ? {...car, media: action.payload.files} : car))
         })
         .addCase(addMedia.fulfilled, (state, action) => {
            state.selectedCar = {...state.selectedCar, media: [...state.selectedCar.media, action.payload.file]}
            state.all = state.all.map(car => (car.id === action.payload.car_id ? {...car, media: [...car.media, action.payload.file]} : car))
         })      
         .addCase(removeMedia.fulfilled, (state, action) => {
            if (action.payload.status === 200) {
               const media = state.selectedCar.media.filter(item => item.file_id !== action.payload.id)
               state.selectedCar = {...state.selectedCar, media}
               state.all = state.all.map(car => (car.id === action.payload.car_id ? {...car, media} : car))
            }
         })
         .addCase(getCarBrands.fulfilled, (state, action) => {
            state.brands = action.payload
         })
         .addCase(getOffices.fulfilled, (state, action) => {
            state.offices = action.payload
         })
         .addCase(getAccidents.fulfilled, (state, action) => {
            state.accidents = action.payload
         })
         .addCase(changeCarStatus.fulfilled, (state, action) => {
            state.selectedCar = action.payload
            state.all = state.all.map(car => (car.id === action.payload.id ? {...action.payload, ttl: new Date} : car))
         })
         .addCase(getReglamentRecords.fulfilled, (state, action) => {
            state.data = action.payload.data
            state.params = action.payload.params
            state.total = action.payload.totalPages
            if (Array.isArray(action.payload.types)) {
               state.reglamentRecordTypes = action.payload.types
            }
         })
         .addCase(getReglamentExpiredRecords.fulfilled, (state, action) => {
            state.data = action.payload.data
            state.params = action.payload.params
            state.total = action.payload.totalPages
         })
         .addCase(getCarsMaintenanceWorks.fulfilled, (state, action) => {
            state.maintenances = action.payload.data
            state.params = action.payload.params
            state.total = action.payload.totalPages
         })
         .addCase(getCarsUsedParts.fulfilled, (state, action) => {
            state.parts = action.payload.data
            state.params = action.payload.params
            state.total = action.payload.totalPages
         })
         .addCase(getReglamentSettings.fulfilled, (state, action) => {            
            if (action.payload.length > 0) {
               state.reglamentSettings = action.payload.reduce((acc, item) => {
                  acc[item.var] = item.value
                  return acc
               }, {})
            } else {
               state.reglamentSettings = null
            }
         })
         .addCase(getReglamentTypes.fulfilled, (state, action) => {
            state.reglamentTypes = Array.isArray(action.payload?.types) ? action.payload.types : null
         })
         .addCase(updateReglamentSettings.fulfilled, (state) => {
            // ** System types are built from settings, so they have to be reloaded
            state.reglamentSettings = null
            state.reglamentTypes = null
         })
         .addCase(addReglamentType.fulfilled, (state, action) => {
            if (state.reglamentTypes !== null && action.payload?.code !== undefined) {
               state.reglamentTypes = [...state.reglamentTypes, action.payload]
            }
         })
         .addCase(updateReglamentType.fulfilled, (state, action) => {
            if (state.reglamentTypes !== null && action.payload?.code !== undefined) {
               state.reglamentTypes = state.reglamentTypes.map(item => (item.code === action.payload.code ? action.payload : item))
            }
         })
         .addCase(removeReglamentType.fulfilled, (state, action) => {
            if (state.reglamentTypes !== null && action.payload === true) {
               state.reglamentTypes = state.reglamentTypes.filter(item => item.id !== action.meta.arg)
            }
         })
   }
})

export const {
    changeExpiredQuantity,
    registerReglamentFetch
} = appCarsSlice.actions

export default appCarsSlice.reducer
